import { type Action, createStore } from 'react-sweet-state';
import { produce } from 'immer';
import { cloneDeep } from 'lodash';

import { type AllInstructionsData, EMPTY_ALL_INSTRUCTIONS_DATA } from 'src/data/generation';
import type { ShortFormSection } from 'src/data/shortForm';
import type { ID } from 'src/lib/collection';

export interface State {
  structureInstructionsData: AllInstructionsData;
  rewriteSections: ShortFormSection[];
}
export type Actions = typeof ACTIONS;

const INITIAL_STATE: State = {
  structureInstructionsData: EMPTY_ALL_INSTRUCTIONS_DATA,
  rewriteSections: [],
};

const PRIVATE_ACTIONS = {
  clearSectionsStructureInstructions:
    (): Action<State> =>
    ({ getState, setState }) => {
      const { rewriteSections } = getState();

      const newValue = produce(rewriteSections, (draft) => {
        draft.forEach((section) => {
          section.structureInstructionsData = undefined;
        });
      });

      setState({ rewriteSections: newValue });
    },
};

const ACTIONS = {
  getStructureInstructionsData:
    (): Action<State, void, AllInstructionsData> =>
    ({ getState }) => {
      return getState().structureInstructionsData;
    },

  updateStructureInstructionsData:
    (newData: AllInstructionsData): Action<State> =>
    ({ setState, dispatch }) => {
      setState({ structureInstructionsData: newData });

      // each time user changes top-level SFV value, clear SFV from all sections,
      // so top-level value will be cascaded down
      dispatch(PRIVATE_ACTIONS.clearSectionsStructureInstructions());
    },

  initRewriteSections:
    (sections: ShortFormSection[]): Action<State> =>
    ({ setState }) => {
      setState({ rewriteSections: cloneDeep(sections) });
    },

  getRewriteSections:
    (): Action<State, void, ShortFormSection[]> =>
    ({ getState }) => {
      return getState().rewriteSections;
    },

  getRewriteSection:
    (sectionId: ID): Action<State, void, ShortFormSection | undefined> =>
    ({ getState }) => {
      const { rewriteSections } = getState();
      return rewriteSections.find((section) => section.id === sectionId);
    },

  updateRewriteSection:
    (sectionId: ID, data: Partial<ShortFormSection>): Action<State> =>
    ({ getState, setState }) => {
      const { rewriteSections } = getState();

      const newValue = produce(rewriteSections, (draft) => {
        const index = draft.findIndex((section) => section.id === sectionId);
        if (index !== -1) {
          draft[index] = {
            ...draft[index],
            ...data,
          };
        }
      });

      setState({ rewriteSections: newValue });
    },
};

export const AdvancedStore = createStore<State, Actions>({
  initialState: INITIAL_STATE,
  actions: ACTIONS,
  name: 'view-generation-form-advanced',
});
